/* eslint-disable react/no-unstable-nested-components */
import * as Yup from "yup";
import React, { useState } from "react";
import { Form, Formik, Field as FormikField } from "formik";
import { Link, useHistory } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import { toast } from "react-toastify";

import LockIcon from "assets/icons/lock.svg";
import TextField from "components/layout/form/textField";
import UserIcon from "assets/icons/user.svg";
import {
  applyCpfCnpjInputMask,
  getCpfCnpjLoginFieldError,
} from "utils/validation/cpfCnpjLoginField";
import { getSigninInfo } from "store/actions/signin-actions";
import { handleUserId } from "store/actions/user-actions";
import { normalizeDocumentForPayload } from "utils/formatting/document";
import {
  registerLocation,
  registerValues,
  toggleStep,
} from "store/actions/first-access-mi-actions";
import { registerValuesMembership } from "store/actions/membership-actions";
import { useAuth } from "hooks/authHook";

import FormLoginTemplate from "../FormLoginTemplate";
import { Button, ContainerImgForm, Field, ImgForm } from "./styles";
import { useMFAContext } from "hooks/useMFA";

import {
  requestGetAccessToken,
  requestGetCustomer,
  requestGetCustomersWithDocument,
  requestGetCustomersWithUserId,
  requestGetUser,
  requestOauth,
  requestPostUser,
  resetLocalStorage,
  updateUserCount,
} from "services/authServices";

type AcessarMIProps = {
  setShowModalRestPass: React.Dispatch<React.SetStateAction<boolean>>;
};

const AcessarMI = ({ setShowModalRestPass }: AcessarMIProps): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const {
    signIn,
    setUserInfos,
    updateContacts,
    updateQuestions,
    updateUser,
    getAccessToken,
    getSiteId,
    getCustomerCode,
  } = useAuth();
  const history = useHistory();
  const dispatch = useDispatch();

  const {
    credentials: { token: lsMFAtoken },
    setCredentials,
    setUserId,
    setMFAContacts,
    setMFAQuestions,
  } = useMFAContext();

  const signinSchema = Yup.object().shape({
    password: Yup.string().required("Preencha sua senha"),
  });

  const handleDocument = (event, handleChange): void => {
    applyCpfCnpjInputMask(event);
    handleChange(event);
  };

  const validateDocument = (value): string | undefined =>
    getCpfCnpjLoginFieldError(value);

  const handleSubmit = async ({ login, password }): Promise<any> => {
    try {
      setLoading(true);
      resetLocalStorage();

      login = normalizeDocumentForPayload(login);

      sessionStorage.setItem("@UltragazEmpresarial:X-Mfa-Login", login);

      setCredentials({ login, password, token: "" });

      let accessToken = await requestGetAccessToken();
      getAccessToken(accessToken);
      const token = lsMFAtoken || null;

      const reponseOauth = await requestOauth(
        login,
        password,
        token,
        accessToken,
        dispatch,
        getSigninInfo
      );

      accessToken = reponseOauth.access_token;

      const responseGetUser = await requestGetUser(
        login,
        accessToken,
        dispatch,
        handleUserId
      );

      const responseCustomer = await requestGetCustomersWithDocument(
        accessToken,
        login,
        dispatch,
        registerValues,
        registerValuesMembership
      );

      if (
        Object.keys(responseGetUser).length === 0 &&
        responseCustomer &&
        responseCustomer.customerId
      ) {
        dispatch(
          registerValues({
            document: login,
            customerId: responseCustomer.customerId,
            customerCode: responseCustomer.customerCode,
          })
        );

        await requestPostUser(
          accessToken,
          responseCustomer.customerCode,
          login,
          dispatch,
          registerLocation,
          toggleStep,
          registerValues,
          history
        );
        return null;
      }

      const responseGetCustomer = await requestGetCustomersWithUserId(
        responseGetUser.userId,
        accessToken
      );

      const [mainAddress] =
        responseGetCustomer?.customerAddresses?.filter((addr) => addr?.main) ||
        [];

      signIn({
        loginEmail: responseGetCustomer.email,
        accessToken,
        refreshToken: reponseOauth.refresh_token,
        customerType: "MI",
      });
      setUserInfos({
        city: mainAddress.city,
        stateCode: mainAddress.stateCode,
        customerName: responseGetCustomer.corporateName,
        customerCode: responseGetCustomer.customerCode,
        userName: responseGetCustomer?.corporateName,
        document: login,
        customerId: responseGetCustomer.customerId,
        customerSiteId: mainAddress.siteId,
        customerSiteCode: mainAddress.code,
        customerAddressLine: mainAddress.addressLine,
        userCustomerRelationshipId: null,
        userId: responseGetUser.userId,
        commercialRegion: responseGetCustomer.commercialRegion,
        commercialComplement: mainAddress.complement ?? "",
      });

      await requestGetCustomer(mainAddress.siteId, accessToken);
      await updateUserCount(login, accessToken);

      const search = history.location.search
        ? history.location.search.split("=")
        : null;

      if (search && search.length > 1) {
        history.push(`/${search[1]}`);
      } else {
        history.push("/portal");
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      const erro = error?.response;

      const userId = error?.response?.data?.user?.id || null;
      const code = error?.response?.data?.code;
      const message = error?.response?.data?.message;
      const status = erro?.status;

      setUserId(userId);
      await localStorage.setItem("@UltragazEmpresarial:userIdMFA", userId);

      if (status === 401) {
        if (code === "401.004") {
          setShowModalRestPass(true);
        } else if (code === "401.006") {
          toast.warn(message);
        } else if (code === "401.005") {
          toast.error(message);
        } else if (code === "401.007") {
          if (!userId) {
            toast.error("Usuário ou senha inválido!");
            return false;
          }
          const { contacts, questions, user, customerCode } =
            error?.response?.data || {};

          // Lê o siteId do texto bruto para preservar precisão
          const rawJson = error?.response?.request?.response;
          const siteIdRaw = rawJson?.match(/"siteId"\s*:\s*(\d+)/)?.[1] ?? null;

          if (contacts && questions && user) {
            setMFAContacts(contacts);
            setMFAQuestions(questions);
            updateContacts(contacts);
            updateQuestions(questions);
            updateUser(user);
            getSiteId(siteIdRaw);
            getCustomerCode(customerCode);
          }
          history.push("/validar-token");
          toast.warn("Você precisa fazer uma verificação de duplo fator");
          localStorage.removeItem("@UltragazEmpresarial:X-Mfa-Hash");

          return history.push("/validar-token");
        } else {
          toast.error(message);
        }
      }

      if (status === 400) {
        toast.error("Autenticação inválida");
        return null;
      }
      if (erro) {
        if (status !== 401) {
          toast.error(message);
        }
      } else if (status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }

    return null;
  };

  return (
    <FormLoginTemplate>
      <Formik
        initialValues={{
          login: "",
          password: "",
        }}
        validationSchema={signinSchema}
        onSubmit={handleSubmit}
        render={({ handleChange, handleBlur, values, errors, touched }) => (
          <Form autoComplete="off">
            <Field>
              <FormikField
                name="login"
                validate={validateDocument}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="login"
                    name="login"
                    inputMode="text"
                    label="Entre com seu CPF ou CNPJ *"
                    placeholder="Entre com seu CPF ou CNPJ *"
                    // @ts-ignore
                    LeadingIcon={() => (
                      <ContainerImgForm>
                        <ImgForm src={UserIcon} width={25} />
                      </ContainerImgForm>
                    )}
                    type="text"
                    errorCondition={errors.login && touched.login}
                    errorMessage={errors.login}
                    isCorrect={!errors.login && !touched.login}
                    onBlur={handleBlur}
                    value={values.login}
                    onChange={(e) => {
                      handleDocument(e, handleChange);
                    }}
                  />
                )}
              />
            </Field>
            <Field>
              <TextField
                id="passwordMi"
                name="password"
                label="Insira sua senha *"
                placeholder="Insira sua senha *"
                LeadingIcon={() => (
                  <ContainerImgForm>
                    <ImgForm src={LockIcon} width={17} />
                  </ContainerImgForm>
                )}
                showChangeTextPassword
                type="password"
                // @ts-ignore
                errorCondition={errors.password && touched.password}
                // @ts-ignore
                errorMessage={errors.password}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.password}
              />
            </Field>
            <Link to="/esqueci-senha-mi">Esqueci minha senha</Link>
            <Button type="submit" loading={loading}>
              Fazer Login
            </Button>
          </Form>
        )}
      />
    </FormLoginTemplate>
  );
};

export default connect()(AcessarMI);
